import React, { useState, useMemo } from 'react';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
} from 'recharts';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';
import { SkeletonChart } from '../shared/Skeleton';
import ThemedChartTooltip, { ThemedTooltipBox } from '../shared/ThemedChartTooltip';
import { ErrorState as SharedErrorState, EmptyState as SharedEmptyState } from '../shared/StateMessage';

/* ----------------------------- constants ----------------------------- */
const GEO_URL = 'https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json';

const STATE_ABBR = {
  Alabama: 'AL', Alaska: 'AK', Arizona: 'AZ', Arkansas: 'AR', California: 'CA',
  Colorado: 'CO', Connecticut: 'CT', Delaware: 'DE', Florida: 'FL', Georgia: 'GA',
  Hawaii: 'HI', Idaho: 'ID', Illinois: 'IL', Indiana: 'IN', Iowa: 'IA',
  Kansas: 'KS', Kentucky: 'KY', Louisiana: 'LA', Maine: 'ME', Maryland: 'MD',
  Massachusetts: 'MA', Michigan: 'MI', Minnesota: 'MN', Mississippi: 'MS', Missouri: 'MO',
  Montana: 'MT', Nebraska: 'NE', Nevada: 'NV', 'New Hampshire': 'NH', 'New Jersey': 'NJ',
  'New Mexico': 'NM', 'New York': 'NY', 'North Carolina': 'NC', 'North Dakota': 'ND', Ohio: 'OH',
  Oklahoma: 'OK', Oregon: 'OR', Pennsylvania: 'PA', 'Rhode Island': 'RI', 'South Carolina': 'SC',
  'South Dakota': 'SD', Tennessee: 'TN', Texas: 'TX', Utah: 'UT', Vermont: 'VT',
  Virginia: 'VA', Washington: 'WA', 'West Virginia': 'WV', Wisconsin: 'WI', Wyoming: 'WY',
  'District of Columbia': 'DC',
};

const DONUT_COLORS = ['#32B1FF', '#6366f1', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316', '#64748b'];
const BAR_COLORS = ['#32B1FF', '#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

const HOUR_LABELS = [
  '12am','1am','2am','3am','4am','5am','6am','7am','8am','9am','10am','11am',
  '12pm','1pm','2pm','3pm','4pm','5pm','6pm','7pm','8pm','9pm','10pm','11pm',
];

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/* ----------------------------- shared ----------------------------- */
const LoadingSkeleton = ({ title }) => (
  <div className="bg-white dark:bg-gray-900 rounded-xl shadow border dark:border-gray-800 p-5">
    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">{title}</h3>
    <SkeletonChart height={240} />
  </div>
);

// Card chrome (title + shell) is bespoke to match the sibling chart cards; only the
// inner message body is delegated to the shared component (like CI's card sites).
const EmptyState = ({ title }) => (
  <div className="bg-white dark:bg-gray-900 rounded-xl shadow border dark:border-gray-800 p-5">
    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">{title}</h3>
    <SharedEmptyState message="No data for this period" padding="py-12" />
  </div>
);

// Fetch-failure counterpart to EmptyState — same card chrome, red inset body.
const ChartErrorState = ({ title, message }) => (
  <div className="bg-white dark:bg-gray-900 rounded-xl shadow border dark:border-gray-800 p-5">
    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">{title}</h3>
    <SharedErrorState message={message} variant="inset" padding="py-12" />
  </div>
);

const ChartCard = ({ title, children }) => (
  <div className="bg-white dark:bg-gray-900 rounded-xl shadow border dark:border-gray-800 p-5 max-[499px]:px-2 h-full flex flex-col">
    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4 max-[499px]:px-3">{title}</h3>
    {children}
  </div>
);

/* ============================================================
   1. Channel Breakdown Donut
   ============================================================ */
const ChannelBreakdownDonut = ({ data, loading }) => {
  if (loading) return <LoadingSkeleton title="Channel Breakdown" />;
  if (!Array.isArray(data) || data.length === 0) return <EmptyState title="Channel Breakdown" />;

  const chartData = data.map((row) => ({
    name: row.channel || 'Other',
    value: Number(row.sessions) || 0,
  })).filter(datum => datum.value > 0);

  const total = chartData.reduce((sum, datum) => sum + datum.value, 0);

  return (
    <ChartCard title="Channel Breakdown">
      <ResponsiveContainer width="100%" height={280}>
        <PieChart>
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            innerRadius={60}
            outerRadius={100}
            paddingAngle={2}
            dataKey="value"
          >
            {chartData.map((_, index) => (
              <Cell key={index} fill={DONUT_COLORS[index % DONUT_COLORS.length]} />
            ))}
          </Pie>
          <ThemedChartTooltip
            content={({ active, payload }) => {
              if (!active || !payload?.length) return null;
              const datum = payload[0];
              const pct = total > 0 ? ((datum.value / total) * 100).toFixed(1) : 0;
              return (
                <ThemedTooltipBox title={datum.name}>
                  <div>{datum.value.toLocaleString()} sessions ({pct}%)</div>
                </ThemedTooltipBox>
              );
            }}
          />
        </PieChart>
      </ResponsiveContainer>
      <div className="flex flex-wrap gap-2 mt-2 justify-center">
        {chartData.map((datum, index) => (
          <div key={datum.name} className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400">
            <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: DONUT_COLORS[index % DONUT_COLORS.length] }} />
            {datum.name}
          </div>
        ))}
      </div>
    </ChartCard>
  );
};

/* ============================================================
   2. Device Split Donut
   ============================================================ */
const DeviceSplitDonut = ({ data, loading }) => {
  if (loading) return <LoadingSkeleton title="Device Split" />;
  if (!Array.isArray(data) || data.length === 0) return <EmptyState title="Device Split" />;

  const chartData = data.map((row) => ({
    name: row.device || 'Unknown',
    value: Number(row.sessions) || 0,
  })).filter(datum => datum.value > 0);

  const total = chartData.reduce((sum, datum) => sum + datum.value, 0);

  return (
    <ChartCard title="Device Split">
      <ResponsiveContainer width="100%" height={280}>
        <PieChart>
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            innerRadius={60}
            outerRadius={100}
            paddingAngle={2}
            dataKey="value"
          >
            {chartData.map((_, index) => (
              <Cell key={index} fill={DONUT_COLORS[index % DONUT_COLORS.length]} />
            ))}
          </Pie>
          <ThemedChartTooltip
            content={({ active, payload }) => {
              if (!active || !payload?.length) return null;
              const datum = payload[0];
              const pct = total > 0 ? ((datum.value / total) * 100).toFixed(1) : 0;
              return (
                <ThemedTooltipBox title={datum.name}>
                  <div>{datum.value.toLocaleString()} sessions ({pct}%)</div>
                </ThemedTooltipBox>
              );
            }}
          />
        </PieChart>
      </ResponsiveContainer>
      <div className="flex flex-wrap gap-2 mt-2 justify-center">
        {chartData.map((datum, index) => (
          <div key={datum.name} className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400">
            <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: DONUT_COLORS[index % DONUT_COLORS.length] }} />
            {datum.name}
          </div>
        ))}
      </div>
    </ChartCard>
  );
};

/* ============================================================
   3. New vs Returning Users Bar
   ============================================================ */
const NewVsReturningBar = ({ data, loading }) => {
  if (loading) return <LoadingSkeleton title="New vs Returning Users" />;
  if (!Array.isArray(data) || data.length === 0) return <EmptyState title="New vs Returning Users" />;

  const chartData = data
    .filter(row => row.user_type)
    .map(row => ({
      // 'new'/'returning' get friendly labels; any other bucket renders raw as stored.
      name: row.user_type === 'new' ? 'New Visitor' : row.user_type === 'returning' ? 'Returning Visitor' : row.user_type,
      sessions: Number(row.sessions) || 0,
      users: Number(row.users) || 0,
      conversions: Number(row.conversions) || 0,
    }));

  return (
    <ChartCard title="New vs Returning Users">
      <ResponsiveContainer width="100%" height={280}>
        <BarChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#374151" vertical={false} />
          <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={{ stroke: '#374151' }} tickLine={false} />
          <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} axisLine={false} tickLine={false} width={50} allowDecimals={false} />
          <ThemedChartTooltip content={({ active, payload, label }) => {
            if (!active || !payload?.length) return null;
            return (
              <ThemedTooltipBox title={label}>
                {payload.map(entry => (
                  <div key={entry.dataKey}>{entry.dataKey}: {entry.value.toLocaleString()}</div>
                ))}
              </ThemedTooltipBox>
            );
          }} />
          <Legend wrapperStyle={{ fontSize: 11, color: '#9ca3af' }} />
          <Bar dataKey="sessions" fill="#32B1FF" radius={[4, 4, 0, 0]} />
          <Bar dataKey="users" fill="#6366f1" radius={[4, 4, 0, 0]} />
          <Bar dataKey="conversions" fill="#10b981" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
};

/* ============================================================
   4. Traffic by Hour Bar
   ============================================================ */
const TrafficByHourBar = ({ data, loading, error }) => {
  if (error) return <ChartErrorState title="Traffic by Hour" message={error} />;
  if (loading) return <LoadingSkeleton title="Traffic by Hour" />;
  if (!Array.isArray(data) || data.length === 0) return <EmptyState title="Traffic by Hour" />;

  const chartData = data.map(row => ({
    hour: row.hour == null ? 'Unknown' : (HOUR_LABELS[row.hour] || `${row.hour}`),
    sessions: Number(row.sessions) || 0,
    users: Number(row.users) || 0,
    engaged_sessions: Number(row.engaged_sessions) || 0,
    conversions: Number(row.conversions) || 0,
  }));

  return (
    <ChartCard title="Traffic by Hour">
      <div className="rotate-x-labels">
      <ResponsiveContainer width="100%" height={280}>
        <BarChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#374151" vertical={false} />
          <XAxis dataKey="hour" tick={{ fontSize: 10, fill: '#9ca3af' }} axisLine={{ stroke: '#374151' }} tickLine={false} interval="preserveStartEnd" minTickGap={14} />
          <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} axisLine={false} tickLine={false} width={50} allowDecimals={false} />
          <ThemedChartTooltip content={({ active, payload, label }) => {
            if (!active || !payload?.length) return null;
            const datum = payload[0].payload;
            return (
              <ThemedTooltipBox title={label}>
                <div>Sessions: {datum.sessions.toLocaleString()}</div>
                <div>Users: {datum.users.toLocaleString()}</div>
                <div>Engaged: {datum.engaged_sessions.toLocaleString()}</div>
                <div>Conversions: {datum.conversions.toLocaleString()}</div>
              </ThemedTooltipBox>
            );
          }} />
          <Bar dataKey="sessions" fill="#32B1FF" radius={[2, 2, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
      </div>
    </ChartCard>
  );
};

/* ============================================================
   5. Traffic by Day of Week Bar
   ============================================================ */
const TrafficByDayOfWeekBar = ({ data, loading, error }) => {
  if (error) return <ChartErrorState title="Traffic by Day of Week" message={error} />;
  if (loading) return <LoadingSkeleton title="Traffic by Day of Week" />;
  if (!Array.isArray(data) || data.length === 0) return <EmptyState title="Traffic by Day of Week" />;

  const chartData = data.map(row => ({
    day: row.day_of_week == null ? 'Unknown' : (DAY_LABELS[row.day_of_week] || `Day ${row.day_of_week}`),
    sessions: Number(row.sessions) || 0,
    users: Number(row.users) || 0,
    engaged_sessions: Number(row.engaged_sessions) || 0,
    conversions: Number(row.conversions) || 0,
    bounce_rate: Number(row.bounce_rate) || 0,
  }));

  return (
    <ChartCard title="Traffic by Day of Week">
      <div className="rotate-x-labels">
      <ResponsiveContainer width="100%" height={280}>
        <BarChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#374151" vertical={false} />
          <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={{ stroke: '#374151' }} tickLine={false} />
          <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} axisLine={false} tickLine={false} width={50} allowDecimals={false} />
          <ThemedChartTooltip content={({ active, payload, label }) => {
            if (!active || !payload?.length) return null;
            const datum = payload[0].payload;
            return (
              <ThemedTooltipBox title={label}>
                <div>Sessions: {datum.sessions.toLocaleString()}</div>
                <div>Users: {datum.users.toLocaleString()}</div>
                <div>Engaged: {datum.engaged_sessions.toLocaleString()}</div>
                <div>Bounce Rate: {(datum.bounce_rate * 100).toFixed(1)}%</div>
                <div>Conversions: {datum.conversions.toLocaleString()}</div>
              </ThemedTooltipBox>
            );
          }} />
          <Legend wrapperStyle={{ fontSize: 11, color: '#9ca3af' }} />
          <Bar dataKey="sessions" fill="#32B1FF" radius={[4, 4, 0, 0]} />
          <Bar dataKey="users" fill="#6366f1" radius={[4, 4, 0, 0]} />
          <Bar dataKey="conversions" fill="#10b981" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
      </div>
    </ChartCard>
  );
};

/* ============================================================
   6. Top 10 Landing Pages Horizontal Bar
   ============================================================ */
const formatLandingPage = (raw) => {
  if (raw == null || raw === '') return '(unknown)';
  if (raw === '/') return '/ (homepage)';
  return raw;
};

const TopLandingPagesBar = ({ data, loading }) => {
  if (loading) return <LoadingSkeleton title="Top 10 Landing Pages" />;
  if (!Array.isArray(data) || data.length === 0) return <EmptyState title="Top 10 Landing Pages" />;

  const chartData = data
    .slice(0, 10)
    .map(row => {
      const label = formatLandingPage(row.landing_page ?? row.page ?? null);
      return {
        page: label.length > 18 ? label.slice(0, 17) + '…' : label,
        fullPage: label,
        sessions: Number(row.sessions) || 0,
      };
    });

  return (
    <ChartCard title="Top 10 Landing Pages">
      <ResponsiveContainer width="100%" height={Math.max(280, chartData.length * 32)}>
        <BarChart data={chartData} layout="vertical" margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#374151" horizontal={false} />
          <XAxis type="number" tick={{ fontSize: 10, fill: '#9ca3af' }} axisLine={{ stroke: '#374151' }} tickLine={false} allowDecimals={false} />
          <YAxis dataKey="page" type="category" tick={{ fontSize: 10, fill: '#9ca3af' }} axisLine={false} tickLine={false} width={120} />
          <ThemedChartTooltip content={({ active, payload }) => {
            if (!active || !payload?.length) return null;
            const datum = payload[0].payload;
            return (
              <ThemedTooltipBox>
                <div style={{ fontWeight: 600, marginBottom: 4, maxWidth: 300, wordBreak: 'break-all' }}>{datum.fullPage}</div>
                <div>Sessions: {datum.sessions.toLocaleString()}</div>
              </ThemedTooltipBox>
            );
          }} />
          <Bar dataKey="sessions" fill="#32B1FF" radius={[0, 4, 4, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
};

/* ============================================================
   7. Conversions by Channel Bar
   ============================================================ */
const ConversionsByChannelBar = ({ data, loading }) => {
  if (loading) return <LoadingSkeleton title="Conversions by Channel" />;
  if (!Array.isArray(data) || data.length === 0) return <EmptyState title="Conversions by Channel" />;

  const chartData = data
    .map(row => ({
      channel: row.channel || 'Other',
      conversions: Number(row.conversions) || 0,
    }))
    .filter(row => row.conversions > 0)
    .sort((rowA, rowB) => rowB.conversions - rowA.conversions);

  if (chartData.length === 0) return <EmptyState title="Conversions by Channel" />;

  return (
    <ChartCard title="Conversions by Channel">
      <ResponsiveContainer width="100%" height={280}>
        <BarChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#374151" vertical={false} />
          <XAxis dataKey="channel" tick={{ fontSize: 10, fill: '#9ca3af' }} axisLine={{ stroke: '#374151' }} tickLine={false} interval="preserveStartEnd" minTickGap={8} />
          <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} axisLine={false} tickLine={false} width={50} allowDecimals={false} />
          <ThemedChartTooltip content={({ active, payload, label }) => {
            if (!active || !payload?.length) return null;
            return (
              <ThemedTooltipBox title={label}>
                <div>Conversions: {payload[0].value.toLocaleString()}</div>
              </ThemedTooltipBox>
            );
          }} />
          <Bar dataKey="conversions" radius={[4, 4, 0, 0]}>
            {chartData.map((_, index) => (
              <Cell key={index} fill={BAR_COLORS[index % BAR_COLORS.length]} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
};

/* ============================================================
   8. Drive Market Map (US State Choropleth)
   ============================================================ */
const DriveMarketMap = ({ data, loading, error }) => {
  const [tooltip, setTooltip] = useState(null);

  const { stateMap, maxSessions } = useMemo(() => {
    if (!Array.isArray(data) || data.length === 0) return { stateMap: {}, maxSessions: 0 };
    const usRows = data.filter(row =>
      (row.country || '').toLowerCase() === 'united states' ||
      (row.country || '').toLowerCase() === 'us'
    );
    const map = {};
    let max = 0;
    usRows.forEach(row => {
      const region = (row.region || '').toLowerCase();
      if (!region) return;
      const sessions = Number(row.sessions) || 0;
      map[region] = { sessions, users: Number(row.users) || 0 };
      if (sessions > max) max = sessions;
    });
    return { stateMap: map, maxSessions: max };
  }, [data]);

  if (error) return <ChartErrorState title="Drive Market Map" message={error} />;
  if (loading) return <LoadingSkeleton title="Drive Market Map" />;
  if (!Array.isArray(data) || data.length === 0) return <EmptyState title="Drive Market Map" />;

  return (
    <ChartCard title="Drive Market Map">
      <div className="relative">
        <ComposableMap projection="geoAlbersUsa" className="w-full h-auto" style={{ maxHeight: '340px' }}>
          <Geographies geography={GEO_URL}>
            {({ geographies }) =>
              geographies.map((geo) => {
                const name = geo.properties.name;
                const stateDatum = stateMap[name.toLowerCase()];
                const intensity = stateDatum && maxSessions > 0 ? Math.min(stateDatum.sessions / maxSessions, 1) : 0;
                const fill = stateDatum
                  ? `rgba(50, 177, 255, ${0.12 + intensity * 0.88})`
                  : 'rgba(229, 231, 235, 0.5)';
                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill={fill}
                    stroke="#fff"
                    strokeWidth={0.5}
                    style={{
                      default: { outline: 'none' },
                      hover: { outline: 'none', fill: stateDatum ? 'rgba(50, 177, 255, 0.9)' : 'rgba(209, 213, 219, 0.7)' },
                      pressed: { outline: 'none' },
                    }}
                    onMouseEnter={(event) => {
                      setTooltip({ x: event.clientX, y: event.clientY, name, abbr: STATE_ABBR[name] || '', data: stateDatum });
                    }}
                    onMouseMove={(event) => {
                      setTooltip((prev) => prev ? { ...prev, x: event.clientX, y: event.clientY } : null);
                    }}
                    onMouseLeave={() => setTooltip(null)}
                  />
                );
              })
            }
          </Geographies>
        </ComposableMap>

        {tooltip && (
          <div
            className="fixed z-50 pointer-events-none bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg px-3 py-2 text-sm"
            style={{ left: tooltip.x + 12, top: tooltip.y - 10 }}
          >
            <div className="font-semibold text-gray-900 dark:text-gray-100">
              {tooltip.name}{tooltip.abbr ? ` (${tooltip.abbr})` : ''}
            </div>
            {tooltip.data ? (
              <div className="text-gray-500 dark:text-gray-400 text-sm mt-0.5">
                {tooltip.data.sessions.toLocaleString()} sessions · {tooltip.data.users.toLocaleString()} users
              </div>
            ) : (
              <div className="text-gray-400 dark:text-gray-500 text-sm mt-0.5">No sessions</div>
            )}
          </div>
        )}
      </div>
    </ChartCard>
  );
};

// Named exports for embedding charts inside tabs. This module intentionally has no
// default export — the charts are always composed into a tab, never rendered as one
// block. WindowSelector used to live here too; it now lives in
// content-behavior/WindowSelector.js, which delegates to the shared SelectMenu.
export {
  ChannelBreakdownDonut,
  DeviceSplitDonut,
  NewVsReturningBar,
  TrafficByHourBar,
  TrafficByDayOfWeekBar,
  TopLandingPagesBar,
  ConversionsByChannelBar,
  DriveMarketMap,
};
